/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';
import type { ObjectId } from 'mongodb';

import { TasksService } from '../workflows/task/task.service';
import { MonitoringAPIService } from '../../shared/api/monitoring-api.service';
import { PrintAPIService } from '../../shared/api/print-api.service';
import { SalesAPIService } from '../../shared/api/sales-api.service';
import { InterfaceService } from '../../shared/interface/interface.service';
import { EventMonitoringEvent, Task } from '../../src/app/@shared-types/event-monitoring';
import { ExpiringPricingSticker } from '../../src/app/@shared-types/print';
import { VehicleReservation } from '../../src/app/@shared-types/vehicle/availability';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';
import { PermissionsService } from '../authorization/permissions.service';
import { UserRole } from '../../src/app/@types/user';
import { TierService } from '../tenant/tier.service';
import { EFeatures } from '../../src/app/@shared-types/tenant/enums';
import { CustomerModel } from '../../src/app/shared/models/customer/customer';

interface OnlineError {
  vehicleId: ObjectId,
  model: string,
  errorSources: string,
  error: string,
}

interface FrontpageScope extends IScope {
  events: EventMonitoringEvent[] | null,
  eventsTotal: number,
  tasks: Task[] | null,
  tasksTotal: number,
  onlineErrors: OnlineError[] | null,
  dates: {
    title: string,
    description: string,
    date: Date,
    link: string,
    color: string,
  }[] | null,
}

/* @ngInject */
export default function FrontpageController(
  $scope: FrontpageScope,
  toastService: ToastService,
  tasksService: TasksService,
  interfaceService: InterfaceService,
  monitoringApiService: MonitoringAPIService,
  salesApiService: SalesAPIService,
  printApiService: PrintAPIService,
  permissionsService: PermissionsService,
  tierService: TierService,
  customerModel: CustomerModel,
): void {
  const EVENT_LIMIT = 10;

  $scope.events = null;
  $scope.tasks = null;
  $scope.dates = null;
  $scope.onlineErrors = null;

  let reservations: VehicleReservation[] | null = null;
  let pricingStickers: ExpiringPricingSticker[] | null = null;

  const initOnlineErrors = async (): Promise<void> => {
    if (!tierService.authorizedToUseFeature(EFeatures.API_ONLINE_EXPORT)) {
      return;
    }
    if (!permissionsService.isPermitted([UserRole.ONLINE_ADMIN])) return;

    $scope.onlineErrors = [];

    const vehicles = await salesApiService.getVehiclesWithOnlineErrors();
    vehicles.forEach((vehicle) => {
      const errorSources: string[] = [];
      let errors: string[] = [];
      if (vehicle.apiData?.MOBILE_SELLER?.errors) {
        errorSources.push('mobile.de');
        errors = vehicle.apiData.MOBILE_SELLER.errors;
      }

      const error = errors.length > 1 ? `${errors[0]} (${errors.length - 1} weitere Warnungen)` : errors[0];

      $scope.onlineErrors?.push({
        vehicleId: vehicle._id,
        model: `${vehicle.modelData?.make} ${vehicle.modelData?.model} ${vehicle.modelData?.modelDescription}`,
        errorSources: errorSources.join(', '),
        error,
      });
    });
  };

  /**
   * Initialize the latest events.
   */
  const initEvents = () => new Promise((resolve, reject) => {
    if (!tierService.authorizedToUseFeature(EFeatures.WORKFLOW_ADD)) {
      return resolve([]);
    }

    monitoringApiService.apiGetEvents()
      .then((events) => {
        $scope.eventsTotal = events.length;
        $scope.events = events.splice(0, EVENT_LIMIT);
        $scope.eventsTotal /= events.length;
        interfaceService.applyScope($scope);
      })
      .catch((e) => reject(e));
  });

  /**
   * Initialize the latest open tasks by requesting them from the API server.
   */
  const initTasks = (): Promise<void> => new Promise((resolve, reject) => {
    if (!tierService.authorizedToUseFeature(EFeatures.WORKFLOW_ADD)) {
      return resolve();
    }

    tasksService.getOpenTasks()
      .then((res) => {
        $scope.tasks = res.tasks;
        $scope.tasksTotal = res.total;
        interfaceService.applyScope($scope);
        return resolve();
      })
      .catch((e) => reject(new Error(`Fehler beim Abrufen der offenen Aufgaben: ${e}`)));
  });

  const getExpiringPricingStickers = (): Promise<void> => new Promise((resolve, reject) => {
    if (!tierService.authorizedToUseFeature(EFeatures.PRINT_PRICING)) {
      return resolve();
    }

    return printApiService.getExpiringPricingStickers()
      .then((res) => {
        pricingStickers = res;
        return resolve();
      })
      .catch((e) => reject(new Error(`Fehler beim Abrufen der ablaufenden Preisschilder: ${e}`)));
  });

  const getUpcomingReservations = (): Promise<void> => new Promise((resolve, reject) => {
    salesApiService.getUpcomingReservations()
      .then((res) => {
        reservations = res;
        return resolve();
      })
      .catch((e) => reject(new Error(`Fehler beim Abrufen der ablaufenden Reservierungen: ${e}`)));
  });

  const initDates = (): Promise<void> => new Promise((resolve, reject) => {
    const datesRequests = [];
    datesRequests.push(getUpcomingReservations());
    datesRequests.push(getExpiringPricingStickers());

    Promise.all(datesRequests)
      .then(async () => {
        $scope.dates = [];
        if (reservations) {
          for (const reservation of reservations) {
            // eslint-disable-next-line no-await-in-loop
            const customerName = await customerModel.getName(reservation.customerId);

            $scope.dates.push({
              title: `Reservierung von ${customerName} läuft aus`,
              description: '',
              date: reservation.endDate,
              link: `/vehicles/stock?id=${reservation.vehicleId}`,
              color: 'border-orange',
            });
          }
        }

        if (pricingStickers) {
          for (const pricingSticker of pricingStickers) {
            $scope.dates.push({
              title: 'Preisschild verliert Gültigkeit',
              description: 'Klicke, um zum Fahrzeug zu gelangen',
              date: pricingSticker.expiryDate,
              link: pricingSticker.link,
              color: 'border-blue',
            });
          }
        }

        $scope.dates = $scope.dates.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });

        return resolve();
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, e);
        return reject();
      });
  });

  const init = () => {
    const frontpageRequests = [];
    frontpageRequests.push(initEvents());
    frontpageRequests.push(initTasks());
    frontpageRequests.push(initDates());
    frontpageRequests.push(initOnlineErrors());

    Promise.all(frontpageRequests)
      .then(() => {
        interfaceService.applyScope($scope);
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, e);
      });
  };

  init();
}
